import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight, Pause, Play, Sparkles, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './BannerSlider.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const AUTOPLAY_DELAY = 5500;

const BannerSlider = ({ position = 'top', mode = 'inline' }) => {
    const navigate = useNavigate();
    const [banners, setBanners] = useState([]);
    const [current, setCurrent] = useState(0);
    const [isManuallyPaused, setIsManuallyPaused] = useState(false);
    const [isInteracting, setIsInteracting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isDismissed, setIsDismissed] = useState(() => (
        mode === 'popup' && window.sessionStorage.getItem('matrimony-banner-popup-dismissed') === 'true'
    ));
    const pointerStart = useRef(null);
    const didSwipe = useRef(false);

    const dismissPopup = useCallback(() => {
        setIsDismissed(true);
        window.sessionStorage.setItem('matrimony-banner-popup-dismissed', 'true');
    }, []);

    useEffect(() => {
        let mounted = true;

        const fetchBanners = async () => {
            try {
                const response = await axios.get(`${API}/api/banners`, { params: { position } });
                if (mounted) setBanners(response.data.banners || []);
            } catch (error) {
                if (mounted) setBanners([]);
            } finally {
                if (mounted) setLoading(false);
            }
        };

        fetchBanners();
        return () => { mounted = false; };
    }, [position]);

    useEffect(() => {
        setCurrent(index => banners.length ? Math.min(index, banners.length - 1) : 0);
    }, [banners.length]);

    useEffect(() => {
        if (mode !== 'popup' || loading || banners.length === 0 || isDismissed) return undefined;
        const previousOverflow = document.body.style.overflow;
        const handleEscape = event => {
            if (event.key === 'Escape') dismissPopup();
        };
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', handleEscape);
        };
    }, [mode, loading, banners.length, isDismissed, dismissPopup]);

    const showPrevious = useCallback(() => {
        setCurrent(index => (index - 1 + banners.length) % banners.length);
    }, [banners.length]);

    const showNext = useCallback(() => {
        setCurrent(index => (index + 1) % banners.length);
    }, [banners.length]);

    useEffect(() => {
        if (banners.length <= 1 || isManuallyPaused || isInteracting) return undefined;

        const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        if (reducedMotion) return undefined;

        const timer = window.setInterval(showNext, AUTOPLAY_DELAY);
        return () => window.clearInterval(timer);
    }, [banners.length, isManuallyPaused, isInteracting, showNext]);

    const followBannerLink = banner => {
        if (!banner.linkUrl) return;

        if (/^https?:\/\//i.test(banner.linkUrl)) {
            window.open(banner.linkUrl, '_blank', 'noopener,noreferrer');
            return;
        }

        navigate(banner.linkUrl);
    };

    const handleSlideKeyDown = (event, banner) => {
        if (!banner.linkUrl || (event.key !== 'Enter' && event.key !== ' ')) return;
        event.preventDefault();
        followBannerLink(banner);
    };

    const handlePointerDown = event => {
        pointerStart.current = event.clientX;
        didSwipe.current = false;
    };

    const handlePointerUp = event => {
        if (pointerStart.current === null || banners.length <= 1) return;
        const movement = event.clientX - pointerStart.current;
        pointerStart.current = null;
        if (Math.abs(movement) < 45) return;
        didSwipe.current = true;
        movement > 0 ? showPrevious() : showNext();
    };

    if (loading || banners.length === 0 || isDismissed) return null;

    const carousel = (
        <section
            className={`banner-slider banner-slider--${position}${mode === 'popup' ? ' banner-slider--modal' : ''}`}
            aria-label={`${position} promotional banners`}
            aria-roledescription="carousel"
            onMouseEnter={() => setIsInteracting(true)}
            onMouseLeave={() => setIsInteracting(false)}
            onFocusCapture={() => setIsInteracting(true)}
            onBlurCapture={event => {
                if (!event.currentTarget.contains(event.relatedTarget)) setIsInteracting(false);
            }}
        >
            {mode === 'popup' && (
                <button
                    className="banner-slider__close"
                    type="button"
                    onClick={dismissPopup}
                    aria-label="Close promotion"
                >
                    <X size={19} aria-hidden="true" />
                </button>
            )}
            <div
                className="banner-slider__viewport"
                onPointerDown={handlePointerDown}
                onPointerUp={handlePointerUp}
                onPointerCancel={() => { pointerStart.current = null; }}
            >
                <div
                    className="banner-slider__track"
                    style={{ transform: `translateX(-${current * 100}%)` }}
                >
                    {banners.map((banner, index) => (
                        <article
                            className={`banner-slide${banner.linkUrl ? ' is-clickable' : ''}`}
                            key={banner._id || `${banner.title}-${index}`}
                            aria-hidden={index !== current}
                            role={banner.linkUrl ? 'link' : undefined}
                            tabIndex={banner.linkUrl && index === current ? 0 : -1}
                            onClick={() => {
                                if (!didSwipe.current) followBannerLink(banner);
                                didSwipe.current = false;
                            }}
                            onKeyDown={event => handleSlideKeyDown(event, banner)}
                        >
                            {banner.imageUrl && (
                                <img
                                    className="banner-slide__image"
                                    src={banner.imageUrl}
                                    alt={banner.title || 'Gettimelam offer'}
                                    draggable="false"
                                />
                            )}
                            <div className="banner-slide__shade" />
                            {(banner.title || banner.subtitle) && (
                                <div className="banner-slide__content">
                                    <span className="banner-slide__eyebrow"><Sparkles size={13} /> Featured for you</span>
                                    {banner.title && <h2>{banner.title}</h2>}
                                    {banner.subtitle && <p>{banner.subtitle}</p>}
                                    {banner.linkUrl && <span className="banner-slide__cta">Explore now <ArrowRight size={15} /></span>}
                                </div>
                            )}
                        </article>
                    ))}
                </div>
            </div>

            {banners.length > 1 && (
                <>
                    <button
                        className="banner-slider__arrow banner-slider__arrow--previous"
                        type="button"
                        onClick={showPrevious}
                        aria-label="Show previous banner"
                    >
                        <ChevronLeft aria-hidden="true" />
                    </button>
                    <button
                        className="banner-slider__arrow banner-slider__arrow--next"
                        type="button"
                        onClick={showNext}
                        aria-label="Show next banner"
                    >
                        <ChevronRight aria-hidden="true" />
                    </button>

                    <div className="banner-slider__controls">
                        <div className="banner-slider__dots" aria-label="Choose a banner">
                            {banners.map((banner, index) => (
                                <button
                                    type="button"
                                    key={banner._id || index}
                                    className={`banner-slider__dot${index === current ? ' is-active' : ''}`}
                                    onClick={() => setCurrent(index)}
                                    aria-label={`Show banner ${index + 1}: ${banner.title || 'Promotion'}`}
                                    aria-current={index === current ? 'true' : undefined}
                                />
                            ))}
                        </div>
                        <span className="banner-slider__count" aria-live="polite">
                            {String(current + 1).padStart(2, '0')} / {String(banners.length).padStart(2, '0')}
                        </span>
                        <button
                            type="button"
                            className="banner-slider__pause"
                            onClick={() => setIsManuallyPaused(paused => !paused)}
                            aria-label={isManuallyPaused ? 'Play banner slideshow' : 'Pause banner slideshow'}
                        >
                            {isManuallyPaused ? <Play size={14} fill="currentColor" aria-hidden="true" /> : <Pause size={14} fill="currentColor" aria-hidden="true" />}
                        </button>
                    </div>
                </>
            )}
        </section>
    );

    if (mode === 'popup') {
        return (
            <div
                className="banner-popup"
                role="dialog"
                aria-modal="true"
                aria-label="Featured promotion"
                onClick={event => {
                    if (event.target === event.currentTarget) dismissPopup();
                }}
            >
                {carousel}
            </div>
        );
    }

    return carousel;
};

export default BannerSlider;
